import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const MAX_POLLS = 15;

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('da-DK', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return ''; }
};

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState('polling'); // polling | paid | timeout | error
  const [accessInfo, setAccessInfo] = useState(null);
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setStatus('paid');
          // P008: fetch access status (return value 1-4) via /auth/me
          try {
            const meRes = await api.get('/auth/me');
            setAccessInfo(meRes.data.access_status || null);
          } catch (e) { console.debug('me fetch failed:', e); }
          await refresh();
          return;
        }
        if (['expired', 'failed'].includes(data.payment_status)) {
          setStatus('error');
          return;
        }
      } catch (e) { console.debug('payment status poll error:', e); }
      pollsRef.current += 1;
      if (pollsRef.current >= MAX_POLLS) { setStatus('timeout'); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  // Build the confirmation message from P008 return value
  const successMessage = (() => {
    if (!accessInfo) return 'Dit abonnement er nu aktivt.';
    const code = accessInfo.code;
    const minutes = accessInfo.remaining_paid_minutes || 0;
    const exp = accessInfo.expire_date;
    if (code === 1 && exp) return `Din adgang er aktiv til ${fmtDate(exp)}.`;
    if (code === 2 && exp) return `Din adgang er aktiv til ${fmtDate(exp)} — plus ${minutes} minutters taletid.`;
    if (code === 4) return `Du har ${minutes} minutters taletid til rådighed.`;
    if (code === 3) return 'Din adgang er udløbet.';
    return 'Dit abonnement er nu aktivt.';
  })();

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16">
      <div className="max-w-md w-full text-center rounded-lg border border-border bg-[#18181B] p-10">
        {status === 'polling' && (
          <>
            <Loader2 className="w-12 h-12 text-volt mx-auto mb-6 animate-spin" />
            <h1 className="font-display text-3xl font-bold mb-2">Bekræfter betaling…</h1>
            <p className="text-muted-foreground">Vent venligst.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <CheckCircle2 className="w-12 h-12 text-volt mx-auto mb-6" data-testid="payment-success-icon" />
            <h1 className="font-display text-3xl font-bold mb-2">Betaling godkendt!</h1>
            <p className="text-muted-foreground mb-6" data-testid="payment-success-expire">
              {successMessage}
            </p>
            <Button onClick={() => navigate('/dashboard')}
              className="bg-volt text-black hover:bg-volt font-semibold"
              data-testid="go-to-dashboard-btn">
              Til oversigt
            </Button>
          </>
        )}
        {status === 'timeout' && (
          <>
            <h1 className="font-display text-3xl font-bold mb-2">Behandler stadig…</h1>
            <p className="text-muted-foreground mb-6">Din betaling behandles. Tjek din historik igen om et øjeblik.</p>
            <Button onClick={() => navigate('/payment')} className="bg-volt text-black hover:bg-volt font-semibold">
              Til betaling
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <h1 className="font-display text-3xl font-bold mb-2">Noget gik galt</h1>
            <p className="text-muted-foreground mb-6">Vi kunne ikke bekræfte din betaling.</p>
            <Button onClick={() => navigate('/payment')} className="bg-volt text-black hover:bg-volt font-semibold">
              Prøv igen
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
